import { ComponentProps, ReactNode } from 'react';
import { Toast as RToast } from 'radix-ui';
import { CircleButton, MaterialSymbol, withStyles } from 'vopay';
import styles from './Toast.module.scss';

const cn = withStyles(styles);

export type ToastVariant = 'neutral' | 'success' | 'error';

const icons: Record<ToastVariant, ComponentProps<typeof MaterialSymbol>['icon']> = {
  neutral: 'info',
  success: 'check_circle',
  error: 'error',
};

const closeButtonColorSchemes: Record<ToastVariant, ComponentProps<typeof CircleButton>['colorScheme']> = {
  neutral: 'neutral',
  success: 'success',
  error: 'destructive',
};

export type ToastProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  subTitle?: ReactNode;
  renderRight?: (args: { dismiss: () => void }) => ReactNode;
  closeButton?: boolean;
  variant?: ToastVariant;
};

// Adapted from crossborder-iframe's UI/Toast. Pure UI: the provider in #shared/providers/Toast owns
// the open state and queueing.
export default function Toast({
  isOpen,
  onOpenChange,
  title,
  subTitle,
  renderRight,
  closeButton = true,
  variant = 'neutral',
}: ToastProps) {
  return (
    <RToast.Root
      data-testid="toast"
      className={cn('toastCard', `variant-${variant}`)}
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <div className={cn('toastLeft')}>
        <MaterialSymbol icon={icons[variant]} size="24px" fillAmount={1} className={cn('toastIcon')} />

        <div>
          <RToast.Title className={cn('toastTitle')}>{title}</RToast.Title>
          {subTitle && <RToast.Description className={cn('toastSubTitle')}>{subTitle}</RToast.Description>}
        </div>
      </div>

      <div className={cn('toastRight')}>
        {renderRight?.({ dismiss: () => onOpenChange(false) })}

        {closeButton && (
          <RToast.Close asChild>
            <CircleButton
              circleFill={false}
              icon="close"
              className={cn('closeButton')}
              colorScheme={closeButtonColorSchemes[variant]}
              size="large"
            />
          </RToast.Close>
        )}
      </div>
    </RToast.Root>
  );
}

Toast.Action = RToast.Action;

Toast.Provider = RToast.Provider;

Toast.Viewport = function Viewport(props: ComponentProps<typeof RToast.Viewport>) {
  return <RToast.Viewport {...props} className={cn('toastViewport')} />;
};
